<template>
  <div class="modal modal-goals" tabindex="-1" role="dialog" :class="{ 'show': showModal }">
    <div class="modal-dialog modal-lg personal-goal" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Personal Goal</h5>
          <button type="button" class="close" @click="showModal = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="savePersonalGoal(editingPersonalGoal ? 'edit' : 'add')" class="personal-goals-form">
            <div class="form-row mx-0">
              <!-- User Account Selection -->
              <div class="form-group col-md-4 pl-0">
                <label for="user">User</label>
                <v-select
                  v-model="newPersonalGoal.user_id"
                  class="goal-select-options"
                  placeholder="Select User"
                  id="user"
                  label="name"
                  :options="users"
                  :reduce="user => user.id"
                />
              </div>

              <!-- Goal Input -->
              <div class="form-group col-md-2 pl-0">
                <label for="goals">Goal</label>
                <input type="number" id="goals" class="form-control" v-model="newPersonalGoal.goals" placeholder="Goal" min="1" step="1">
              </div>

              <!-- Submit and Cancel Buttons -->
              <div :class="['form-group', editingPersonalGoal ? 'col-md-4' : 'col-md-6']">
                <button type="submit" class="btn btn-success text-nowrap w-100" :disabled="isLoading">
                  {{ isLoading ? 'Saving data..' : (editingPersonalGoal ? 'Update Personal Goal' : 'Create Personal Goal') }}
                </button>
              </div>

              <div v-if="editingPersonalGoal" class="form-group col-md-2">
                <button @click="cancelEdit" type="button" class="btn btn-secondary text-nowrap w-100" style="margin-top: 32px">
                  Cancel
                </button>
              </div>
            </div>
          </form>

          <p v-if="personalGoals.length === 0" class="not-found">
            No goals found. Please add a personal goal.
          </p>

          <div v-else class="personal-goals">
            <div v-if="isLoadingUserGoals && !isLoading" class="loading-spinner text-center">
              <div class="spinner-border" role="status" />
              <small>Loading user goals..</small>
            </div>
            <table id="personal-goals" class="table table-bordered table-branch-goals w-100">
              <thead>
              <tr>
                <th>User Name</th>
                <th>Position</th>
                <th>Office</th>
                <th>Goal's Period</th>
                <th class="text-center">Goal</th>
                <th class="text-center">Action</th>
              </tr>
              </thead>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { mapGetters } from 'vuex';
import { deletePersonalGoal, getPersonalGoals, getUsers, createPersonalGoal, updatePersonalGoal } from '../../../../services/api/goals';
import { formatPeriod } from "../../../../utils/helpers";

import "datatables.net-bs4";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";
import '../../../../../sass/goals-modal.scss';

export default {
  name: 'SetPersonalGoalsModal',
  components: {
    'v-select': vSelect
  },
  props: {
    role: {
      type: Number,
      default: 0
    }
  },
  mounted() {
    this.fetchUsers();
    this.fetchPersonalGoals();
  },
  data() {
    const now = new Date();

    return {
      showModal: false,
      isLoading: false,
      isLoadingUsers: false,
      isLoadingUserGoals: false,
      errorMessage: '',
      users: [],
      personalGoals: [],
      editingPersonalGoal: null,
      currentMonth: String(now.getMonth() + 1).padStart(2, '0'),
      currentYear: now.getFullYear(),
      newPersonalGoal: {
        user_id: "",
        goals: 0
      }
    };
  },
  computed: {
    ...mapGetters('goals', ['availableMonths'])
  },
  methods: {
    formattedPeriod(period) {
      return formatPeriod(period, null, true);
    },
    validatePersonalGoal(goal) {
      return goal.user_id && goal.goals;
    },
    initDataTable() {
      $(document).ready(() => {
        if ($.fn.DataTable.isDataTable('#personal-goals')) {
          $('#personal-goals').DataTable().destroy();
        }

        $('#personal-goals').DataTable({
          dom: '<"top">rt<"bottom d-flex justify-content-between align-items-center"lp>',
          lengthMenu: [5, 10, 25],
          pageLength: 10,
          data: this.personalGoals,
          pagingType: 'simple_numbers',
          autoWidth: true,
          columns: [
            { data: 'user.name', sortable: true },
            { data: 'user.position', sortable: true },
            { data: 'user.branch.name', sortable: true },
            {
              data: 'year',
              sortable: true,
              render: (data, type, row) => {
                return this.formattedPeriod(data + '-' + row.month);
              }
            },
            { data: 'goals', sortable: true },
            {
              data: 'id',
              sortable: false,
              render: (data, type, row) => {
                return `
                  <button class="btn btn-primary btn-xs btn-edit" data-id="${data}">
                    <i class="fa fa-edit"></i> Edit
                  </button>
                  <button class="btn btn-danger btn-xs btn-delete" data-id="${data}">
                    <i class="fa fa-trash"></i>
                  </button>
                `;
              }
            }
          ]
        });

        $('#personal-goals tbody').off('click', 'button').on('click', 'button', (event) => {
          const goal_id = $(event.currentTarget).data('id');

          if ($(event.currentTarget).hasClass('btn-edit')) {
            const personalGoal = this.personalGoals.find(goal => goal.id === goal_id);
            if (personalGoal) {
              this.editPersonalGoal(personalGoal);
            }
          } else if ($(event.currentTarget).hasClass('btn-delete')) {
            this.deletePersonalGoal(goal_id);
          }
        });
      });
    },
    async fetchUsers() {
      try {
        this.isLoadingUsers = true;

        const response = await getUsers(this.role);

        if (response.status === 200) {
          this.users = response.data;
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        this.isLoadingUsers = false;
      }
    },
    async fetchPersonalGoals() {
      try {
        this.isLoadingUserGoals = true;
        const response = await getPersonalGoals(this.role);

        if (response.status === 200) {
          this.personalGoals = response.data;
          this.initDataTable();
        }
      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        } else {
          this.errorMessage = 'An unexpected error occurred';
        }
      } finally {
        this.isLoadingUserGoals = false;
      }
    },
    async savePersonalGoal(type = 'add') {
      try {
        let response;
        this.isLoading = true;

        if (!this.validatePersonalGoal(this.newPersonalGoal)) {
          new Noty({
            type: "warning",
            text: 'Please fill in all fields before saving the personal goal.',
          }).show();
          return;
        }

        const payload = {
          user_id: this.newPersonalGoal.user_id,
          month: this.currentMonth,
          year: this.currentYear,
          goals: this.newPersonalGoal.goals
        }

        if (type === 'add') {
          response = await createPersonalGoal(payload);
        } else if (type === 'edit') {
          response = await updatePersonalGoal(this.editingPersonalGoal.id, payload);
        }
        if (response.status === 200 || response.status === 201) {
          await this.fetchUsers();
          await this.fetchPersonalGoals();

          this.resetNewPersonalGoal();
          this.editingPersonalGoal = null;

          new Noty({
            type: "success",
            text: type === 'add' ? 'Personal goal created successfully' : 'Personal goal updated successfully',
          }).show();
        } else {
          new Noty({
            type: "error",
            text: 'Failed to save Personal goal. Please try again later.',
          }).show();
        }
      } catch (error) {
        console.error('Error saving personal goal:', error);
        new Noty({
          type: "error",
          text: 'Failed to save personal goal. Please try again later.',
        }).show();
      } finally {
        this.isLoading = false;
      }
    },
    async deletePersonalGoal(goal_id) {
      try {
        const response = await deletePersonalGoal(goal_id);

        if (response.status === 200) {
          this.personalGoals = this.personalGoals.filter(goal => goal.id !== goal_id);
          this.initDataTable();

          new Noty({
            type: "success",
            text: 'Personal Goal removed successfully',
          }).show();
        } else {
          new Noty({
            type: "error",
            text: 'Failed to remove personal goal. Please try again later.',
          }).show();
        }
      } catch (error) {
        console.error('Error removing personal goal:', error);

        new Noty({
          type: "error",
          text: 'Failed to remove personal goal. Please try again later.',
        }).show();
      }
    },
    editPersonalGoal(personalGoal) {
      this.editingPersonalGoal = personalGoal;
      this.newPersonalGoal = { ...personalGoal };
    },
    cancelEdit() {
      this.editingPersonalGoal = null;
      this.resetNewPersonalGoal();
    },
    resetNewPersonalGoal() {
      this.newPersonalGoal = {
        user_id: "",
        month: "",
        year: "",
        goals: 0
      };
    }
  }
}
</script>
